import { useEffect, useId, useRef } from "react";
import { CopilotIcon } from "./CopilotIcon";
import "./ai-showcase.css";

export default function CreateWithAiDialog({
  onClose,
  onSetup,
  onExample,
  localOnly = false,
  onGetFullAccess,
  agency,
}: {
  onClose: () => void;
  onSetup: () => void;
  onExample: () => void;
  localOnly?: boolean;
  onGetFullAccess: () => void;
  agency?: { onSetup: () => void; onExample: () => void };
}) {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    const previous = document.activeElement;
    panel.current?.focus();
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", close, true);
    return () => {
      document.removeEventListener("keydown", close, true);
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, [onClose]);
  return (
    <div className="ai-hub-overlay" onClick={onClose}>
      <div
        className="ai-hub"
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={panel}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id={titleId}>Create with AI</h2>
        <p className="ai-hub-subtitle">Bring an idea. Choose where to work with AI.</p>
        <section className="ai-agent-path">
          <div className="ai-path-heading">
            <span className="ai-path-icon" aria-hidden="true">
              <CopilotIcon />
            </span>
            <div>
              <h3>Create with GitHub Copilot</h3>
              <p>In the GitHub Copilot CLI or desktop app</p>
            </div>
            <span className="ai-status">
              {localOnly ? "Full access needed" : "Cloud drawings only"}
            </span>
          </div>
          <p className="ai-path-description">
            Create new drawings saved online in Excalidraw-MS, or edit drawings already saved there.
          </p>
          {localOnly ? (
            <div className="ai-access">
              <p>Creating cloud drawings with an agent requires full Excalidraw-MS access.</p>
              <button type="button" className="ai-text-link" onClick={onGetFullAccess}>
                Get full access
              </button>
            </div>
          ) : (
            <div className="ai-invitation-actions ai-chooser-actions">
              <button type="button" className="ai-text-link" onClick={onSetup}>
                <span className="ai-introduction-setup-label">Set up GitHub Copilot</span>
              </button>
              <button className="ai-text-link" onClick={onExample}>
                Already set up? Create a diagram
              </button>
            </div>
          )}
        </section>
        {agency && (
          <section className="ai-agent-path" style={{ marginTop: 16 }}>
            <div className="ai-path-heading">
              <span className="ai-path-icon" aria-hidden="true">
                <img src="/agency.svg" width="28" height="28" alt="" />
              </span>
              <div>
                <h3>Create with Agency</h3>
                <p>GitHub Copilot CLI, launched through Agency</p>
              </div>
              <span className="ai-status">
                {localOnly ? "Full access needed" : "Cloud drawings only"}
              </span>
            </div>
            <p className="ai-path-description">
              Install Easel from Agency Playground. Drawing tools and skills are included.
            </p>
            {localOnly ? (
              <div className="ai-access">
                <p>Creating cloud drawings with an agent requires full Excalidraw-MS access.</p>
                <button type="button" className="ai-text-link" onClick={onGetFullAccess}>
                  Get full access
                </button>
              </div>
            ) : (
              <div className="ai-invitation-actions ai-chooser-actions">
                <button type="button" className="ai-text-link" onClick={agency.onSetup}>
                  Set up Agency
                </button>
                <button type="button" className="ai-text-link" onClick={agency.onExample}>
                  Already set up? Create a diagram
                </button>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
